/**
 * Super Admin in-app manual: structured sections for search, filters, and deep links.
 * Keep in sync with docs/manuals/SUPER_ADMIN_MANUAL.md
 */

import { createHelpAssistantManualSection } from "./helpAssistantManualSection.js";

export const SUPER_ADMIN_MANUAL_INTRO = {
  title: "Super Admin guide",
  subtitle:
    "Platform operators use this role to onboard practices, manage organizations, and support calling operations. Use the Admin section of the sidebar for global tools; select an organization in the header to unlock organization-scoped menus.",
  loginLanding: "After sign-in you are sent to the platform dashboard.",
  defaultRoute: "/admin",
};

/** @typedef {{ id: string, group: 'platform' | 'organization', menuLabel: string, route: string, keywords: string[], summary: string, howToUse: string[] }} SuperAdminManualSection */

/** @type {SuperAdminManualSection[]} */
export const SUPER_ADMIN_MANUAL_SECTIONS = [
  {
    id: "platform-dashboard",
    group: "platform",
    menuLabel: "Dashboard (Admin)",
    route: "/admin",
    keywords: [
      "admin",
      "home",
      "stats",
      "overview",
      "platform",
      "metrics",
    ],
    summary:
      "Platform-wide snapshot: organizations, activity, and quick links into admin tools.",
    howToUse: [
      "In the sidebar, under **Admin**, click **Dashboard** (or open `/admin`).",
      "Review cards and shortcuts to jump to inquiries, organizations, or support areas.",
      "Use the header **organization** and **location** selectors when you need org-scoped data elsewhere in the app.",
    ],
  },
  {
    id: "inquiries",
    group: "platform",
    menuLabel: "Inquiries",
    route: "/admin/inquiries",
    keywords: [
      "inquiry",
      "onboarding",
      "lead",
      "signup",
      "convert",
      "practice",
    ],
    summary:
      "Review inbound practice interest and convert approved inquiries into organizations and owner credentials (inquiry-based onboarding).",
    howToUse: [
      "Click **Inquiries** in the Admin block (`/admin/inquiries`).",
      "Work each inquiry through your internal workflow: validate details, then convert to an organization when ready.",
      "After conversion, use **Organizations** to verify locations and owner access.",
    ],
  },
  {
    id: "organizations",
    group: "platform",
    menuLabel: "Organizations",
    route: "/admin/organizations",
    keywords: [
      "org",
      "practice",
      "tenant",
      "multi-tenant",
      "accounts",
    ],
    summary:
      "Create and maintain customer organizations, link locations, and support account health across the platform.",
    howToUse: [
      "Open **Organizations** (`/admin/organizations`).",
      "Search or browse the list; open a row to edit details, status, or related configuration per your process.",
      "Pair with **Locations** and **Manage Users** when setting up a new practice.",
    ],
  },
  {
    id: "locations",
    group: "platform",
    menuLabel: "Locations",
    route: "/settings/locations",
    accessLevel: "manage",
    keywords: [
      "location",
      "locations",
      "create location",
      "add location",
      "site",
      "address",
      "pms",
      "map",
    ],
    summary:
      "Create, edit, and delete practice locations for organizations. Organization owners can only view the same page — they cannot add locations.",
    howToUse: [
      "Click Locations under Admin (/settings/locations). Select the organization in the header if needed.",
      "Click Add Location, enter site name, address, phone, email, timezone, and save.",
      "Edit locations, configure Sikka/PMS per site, or deactivate when needed.",
    ],
    faqs: [
      {
        question: "Who can create a location?",
        answer:
          "Super Admin creates locations: [[Locations|/settings/locations]] → Add Location. Owners can view locations but cannot create or edit them.",
      },
    ],
  },
  {
    id: "logs",
    group: "platform",
    menuLabel: "Logs",
    route: "/admin/logs",
    keywords: [
      "logs",
      "audit",
      "activity",
      "history",
      "tracking",
      "sync",
      "integration",
      "sikka",
      "pms",
    ],
    summary:
      "Admin logs hub: integration sync logs (PMS/Sikka patient and appointment sync, token refresh) plus location-level operational history.",
    howToUse: [
      "Choose **Logs** in the Admin list (`/admin/logs`).",
      "Open the **Integration Sync Logs** tab to review PMS sync runs (success/failure, duration, record counts).",
      "Filter by organization, location, sync type, or date to diagnose sync failures.",
      "Use location logs on the same page for site-level activity when investigating support tickets.",
    ],
    faqs: [
      {
        question: "How do I check PMS sync logs?",
        answer:
          "Super Admin: [[Logs|/admin/logs]] → Integration Sync Logs tab. Filter by org/location to see Sikka patient and appointment sync history.",
      },
      {
        question: "Why did patient sync fail?",
        answer:
          "Check [[Logs|/admin/logs]] integration sync entries for error messages. Common causes: expired Sikka token, wrong office credentials, or PMS downtime. Re-run sync from the org's [[PMS integration|/settings/pms]] after fixing credentials.",
      },
    ],
  },
  {
    id: "recycle-bin",
    group: "platform",
    menuLabel: "Recycle Bin",
    route: "/recycle-bin",
    keywords: ["delete", "restore", "soft delete", "recover", "bin", "trash", "recycle"],
    summary:
      "Restore or permanently delete soft-deleted items: stages, treatments, users, SMS folders/templates, call scripts, organizations, and email templates. Super Admin only.",
    howToUse: [
      "Open **Recycle Bin** (`/recycle-bin`) from the Admin sidebar.",
      "Use search and the type dropdown to find deleted items.",
      "Click a row to preview details, then **Restore** to undo soft delete or **Permanently delete** to remove forever.",
    ],
    faqs: [
      {
        question: "How do I restore a deleted treatment or stage?",
        answer:
          "[[Recycle Bin|/recycle-bin]] → filter by type (Treatment or Stage) → select item → Restore.",
      },
      {
        question: "Who can use the Recycle Bin?",
        answer:
          "Super Admin only. Org owners cannot access [[Recycle Bin|/recycle-bin]].",
      },
    ],
  },
  {
    id: "manage-users",
    group: "platform",
    menuLabel: "Manage Users",
    route: "/admin/users",
    keywords: [
      "users",
      "accounts",
      "staff",
      "credentials",
      "access",
    ],
    summary:
      "Platform-level user administration across organizations (distinct from a single org’s Team Members settings).",
    howToUse: [
      "Open **Manage Users** (`/admin/users`).",
      "Locate a user by email or org; adjust role, activation, or assignments according to policy.",
      "Coordinate with **Organizations** when ownership or org membership changes.",
    ],
  },
  {
    id: "manage-agents",
    group: "platform",
    menuLabel: "Manage Agents & TL",
    route: "/admin/agents",
    keywords: [
      "agent",
      "team leader",
      "tl",
      "calling",
      "dialer",
      "queue",
    ],
    summary:
      "Provision and manage outbound calling agents and team leaders, including assignments tied to locations.",
    howToUse: [
      "Go to **Manage Agents & TL** (`/admin/agents`).",
      "Create or edit agent/TL records, link them to the correct locations, and confirm they can sign in to the agent or team-leader experiences.",
    ],
  },
  {
    id: "platform-tickets",
    group: "platform",
    menuLabel: "Tickets (Admin)",
    route: "/admin/tickets",
    keywords: [
      "tickets",
      "support",
      "help desk",
      "internal",
      "escalation",
    ],
    summary:
      "Internal / platform ticket queue for super admin visibility (separate from org **Support** tickets when applicable).",
    howToUse: [
      "Click **Tickets** under Admin (`/admin/tickets`).",
      "Triage open items, assign owners, and resolve or escalate per your support playbook.",
    ],
  },
  {
    id: "recare-dashboard",
    group: "organization",
    menuLabel: "Recare Dashboard",
    route: "/recare-dashboard",
    keywords: [
      "recare",
      "recall",
      "hygiene",
      "retention",
      "dashboard",
      "campaign",
    ],
    summary:
      "Organization-scoped recare analytics and workflow hub. Requires an organization (and typically a location) selected in the header.",
    howToUse: [
      "In the header, open the **organization** dropdown and select a practice.",
      "Optionally narrow to a **location** using the second dropdown.",
      "In the sidebar under **Organization Menu**, click **Recare Dashboard**, or use the **Open page** button in this manual (route `/recare-dashboard`).",
      "Use filters, tables, and actions on the page to monitor recall lists and operational metrics for that context.",
    ],
  },
  {
    id: "reports",
    group: "organization",
    menuLabel: "Report",
    route: "/reports",
    keywords: [
      "report",
      "reports",
      "analytics",
      "export",
      "calling",
    ],
    summary:
      "Reporting view shared with team leaders and agents; super admins use it with a selected org context when available.",
    howToUse: [
      "Select the target **organization** (and location if needed) in the header.",
      "Click **Report** in the Organization Menu (`/reports`).",
      "Adjust report controls on the page to match the date range or cohort you need.",
    ],
  },
  createHelpAssistantManualSection({
    group: "platform",
    supportTicketPath: "/admin/tickets",
  }),
];

export const SUPER_ADMIN_MANUAL_GROUPS = {
  platform: {
    label: "Platform (Admin sidebar)",
    description:
      "Always visible for super admins. Does not require selecting an organization.",
  },
  organization: {
    label: "Organization menu",
    description:
      "Appears after you select an organization in the header. Mirrors owner-facing items that are enabled for super admin.",
  },
};

export function filterSuperAdminManualSections(query, groupFilter) {
  const q = (query || "").trim().toLowerCase();
  return SUPER_ADMIN_MANUAL_SECTIONS.filter((s) => {
    if (groupFilter && groupFilter !== "all" && s.group !== groupFilter) {
      return false;
    }
    if (!q) return true;
    const hay = [
      s.menuLabel,
      s.summary,
      s.route,
      ...s.keywords,
      ...s.howToUse.join(" "),
      ...(s.faqs || []).map((f) => `${f.question} ${f.answer}`).join(" "),
    ]
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });
}
